import React from "react";
import {useTranslation} from "react-i18next";
import {useKeycloak} from "@react-keycloak/web";
import {AxiosError} from "axios";
import {API, createApiConfig} from "../../utils";
import {LoadingBackdrop} from "../common/Loading";
import {ConfirmationDialog} from "../common/dialogs";

interface MunicipalityInputVariableDeleteEntity {
    id: string;
    name: string;
}

interface MunicipalityInputVariableDeleteProps {
    entity: MunicipalityInputVariableDeleteEntity | null;
    onDeleted: () => void;
    onCancel: () => void;
    onFailed: () => void;
}

interface MunicipalityInputVariableDeleteState {
    activeId: string | null;
    name: "hidden" | "confirm" | "deleting";
}

// Admin-only, irreversible: deletes a MunicipalityInputVariable together with all its
// entered input values. Unlike other GenericEntity*Delete flows there is no "related
// entities" 400 case to handle here - the server either performs the deletion or the
// caller isn't authorized, so a plain checkbox-gated confirmation is enough.
export const MunicipalityInputVariableDelete: React.FC<MunicipalityInputVariableDeleteProps> = (
    props: MunicipalityInputVariableDeleteProps
) => {
    //== Init =================================================================
    const [t] = useTranslation("values");
    const {keycloak, initialized} = useKeycloak();
    const initState: MunicipalityInputVariableDeleteState = {
        activeId: props.entity?.id ?? null,
        name: props.entity === null ? "hidden" : "confirm",
    };
    const [state, setState] = React.useState(initState);
    //== Effects ==============================================================
    React.useEffect(() => {
        const id = props.entity?.id ?? null;
        if (id !== state.activeId) {
            setState({activeId: id, name: props.entity === null ? "hidden" : "confirm"});
        } else if (state.name === "deleting") {
            API.delete<void>(`/municipality-input-variables/${state.activeId}`, createApiConfig(keycloak, initialized))
                .then(() => {
                    props.onDeleted();
                })
                .catch((err: AxiosError) => {
                    setState({activeId: null, name: "hidden"});
                    props.onFailed();
                });
        }
    }, [props.entity, state.name]);
    //== Handlers =============================================================
    const handleDeleteConfirm = (): void => {
        setState({...state, name: "deleting"});
    };
    const handleCancel = (): void => {
        setState({activeId: null, name: "hidden"});
        props.onCancel();
    };
    //== Render ===============================================================
    if (state.name === "deleting") {
        return <LoadingBackdrop />;
    }
    return (
        <ConfirmationDialog
            id="delete-municipality-input-variable"
            title={t("values_input.summary.confirm_delete.title")}
            text={t("values_input.summary.confirm_delete.text", {title: props.entity?.name ?? ""})}
            checkbox={t("values_input.summary.confirm_delete.checkbox")}
            open={state.name === "confirm"}
            onAgree={handleDeleteConfirm}
            onDisagree={handleCancel}
        />
    );
};
